<template>
  <div>
      <p class="mt-1">
        A fundamental goal of redistricting is to create equal population
        districts. This helps ensure that each person has equal representation
        in Congress (<a href="https://constitutioncenter.org/blog/constitution-check-what-does-one-person-one-vote-mean-now/" target="_blank">1 person, 1 vote</a>).
      </p>
      
      <div v-if="postgres.chgtype10to20 != 'same'">
      <!--State going from 1 SEAT to 2 (Montana?) -->
      <p v-if="postgres.chgtype10to20 === 'gain' && postgres.seats2020 === 2">{{postgres.statename}} currently has one at-large district. Due to reapportionment it will <strong>increase to 2 districts</strong>.</p>
      <!--State going from 2 SEATS to 1 (Rhode Island?) -->
      <p v-else-if="postgres.chgtype10to20 === 'loss' && postgres.currentseats === 2">{{ postgres.statename }} currently has two districts. Due to reapportionment it will <strong>decrease to one at-large district</strong>.</p>
      <!-- States LOSING / GAINING seats -->
      <p v-else>
        The table above shows how close or not {{ postgres.districtname }} compares to the average 2020 district population in {{ postgres.statename }} of {{ Math.round(postgres.statepop2020 / postgres.seats2020).toLocaleString() }} people, based on the 
        <span v-if="postgres.chgtype10to20 === 'loss' && postgres.change10to20 === -1"><em>loss</em> of 1 seat (from {{ postgres.currentseats }} to {{ postgres.seats2020 }})</span>
        <span v-if="postgres.chgtype10to20 === 'loss' && postgres.change10to20 < -1"><em>loss</em> of {{ Math.abs(postgres.seats2020 - postgres.currentseats) }} seats (from {{ postgres.currentseats }} to {{ postgres.seats2020 }})</span>
        <span v-if="postgres.chgtype10to20 === 'gain' && postgres.change10to20 === 1"><em>gain</em> of 1 seat (from {{ postgres.currentseats }} to {{ postgres.seats2020 }})</span>
        <span v-if="postgres.chgtype10to20 === 'gain' && postgres.change10to20 > 1"><em>gain</em> of {{ postgres.seats2020 - postgres.currentseats }} seats (from {{ postgres.currentseats }} to {{ postgres.seats2020 }})</span>.
        </p>
    </div>

    <div v-else>
      <!-- State staying at 1 at-large district -->
      <span v-if="postgres.currentseats === postgres.seats2020 && postgres.currentseats === 1">
        <p>
          {{ postgres.statename }} has a <strong>single at-large district</strong>. The state will not gain new seats based on reapportionment.
        </p>
      </span>

      <span v-else>
        <!-- District AT / ABOVE / BELOW current avg -->
        <p>{{ postgres.statename }} will have the same # of congressional districts ({{ postgres.seats2020 }}) after <a href="https://www.census.gov/newsroom/press-kits/2021/2020-census-apportionment-counts.html" target="_blank">post-2020 apportionment</a>. The table above indicates that {{ postgres.districtname }}'s population is <span v-if="postgres.deviation >= -0.001 && postgres.deviation <= 0.001"> equal or close to the "ideal" (i.e., average) district population. <strong>It may not have to expand or shrink during redistricting.</strong> Other factors, however, may require its boundaries to change.</span><span v-if="postgres.deviation > 0.001"> <em>above</em> the "ideal" (i.e., average) district population, meaning that the district may need to <strong><em>shrink</em> by approximately {{ Math.abs(postgres.popdifffromavg).toLocaleString() }} people</strong> during redistricting.</span><span v-if="postgres.deviation < -0.001"> <em>below</em> the "ideal" (i.e., average) district population, meaning that the district may need to <strong><em>expand</em> by approximately {{ Math.abs(postgres.popdifffromavg).toLocaleString() }} people</strong> during redistricting.</span></p>
      </span>
    </div>

    <span>
      Learn more about redistricting in {{ postgres.statename }}:
    </span>
    <ul class="mb-2">
      <li>{{ postgres.stusps }}'s <a v-bind:href="'https://redistricting.lls.edu/state/' + postgres.statename.replace(' ','-') + '/?cycle=2020&level=Congress'" target="_blank">process for redrawing congressional districts</a> <span class="text-caption">(via <a href="https://redistricting.lls.edu/" target="_blank">All About Redistricting</a>)</span>;
      </li>
      <li>how the <a v-bind:href="'https://gerrymander.princeton.edu/reforms/' + postgres.stusps" target="_blank">latest Census-related redistricting timeline delays</a> might impact {{ postgres.stusps }} <span class="text-caption">(via the <a href="https://gerrymander.princeton.edu/" target="_blank">Princeton Gerrymandering Project</a>)</span>; and
      </li>
      <li>
        <a v-bind:href="'https://planscore.org/' + postgres.statename.toLowerCase().replace(' ','_') + '/#!2018-plan-ushouse-eg'" target="_blank">metrics to evaluate {{ postgres.stusps }}'s current districts</a> <span class="text-caption">(via <a href="https://planscore.org/" target="_blank">PlanScore</a>)</span>.
      </li>
    </ul>

<!-- POPULATION CHANGE SECTION (moved from separate Vue template for now) -->
        <v-app-bar color="#c0d7e4" dense height="30px" flat>
      <span class="text-uppercase" style="color: #1d3557; font-weight: 500">Population change</span>
    </v-app-bar>
    <p class="mt-1">
      <!-- <span v-if="postgres.popchg1020 > 0"> -->
      <span v-if="postgres.popchgpct1020 > 0.001">
        Between 2010 and 2020,
        <span class="text-popincrease-color font-weight-bold">{{ postgres.districtname }}'s population grew by
          {{ ((postgres.popchgpct1020) * 100).toFixed(1) }}% (an
          increase of
          {{ Math.round(postgres.popchg1020).toLocaleString() }} people from
          {{ Math.round(postgres.pop2010).toLocaleString() }})</span>.
      </span>
      <!-- <span v-else-if="postgres.popchg1020 < 0"> -->
      <span v-else-if="postgres.popchgpct1020 < -0.001">
        Between 2010 and 2020,
        <span class="text-popdecrease-color font-weight-bold">{{ postgres.districtname }}'s population fell by
          {{ Math.abs((postgres.popchgpct1020) * 100).toFixed(1) }}% (a decrease of
          {{ Math.abs(Math.round(postgres.popchg1020)).toLocaleString() }}
          people from {{ Math.round(postgres.pop2010).toLocaleString() }})</span>.
      </span>
      <!-- <span v-else>
        Between 2010 and 2020, {{ postgres.districtname }}'s population neither increased nor decreased.
      </span> -->
      <span v-else-if="postgres.popchgpct1020 >= -0.001 && postgres.popchgpct1020 < 0">
        Between 2010 and 2020, <strong>{{ postgres.districtname }}'s population effectively did not change</strong> (only decreasing less than 0.1%, or {{ Math.abs(Math.round(postgres.popchg1020)).toLocaleString() }} from {{ Math.round(postgres.pop2010).toLocaleString() }} people).
      </span>
      <span v-else-if="postgres.popchgpct1020 > 0 && postgres.popchgpct1020 < 0.001">
        Between 2010 and 2020, <strong>{{ postgres.districtname }}'s population effectively did not change</strong> (only increasing less than 0.1%, or {{ Math.abs(Math.round(postgres.popchg1020)).toLocaleString() }} from {{ Math.round(postgres.pop2010).toLocaleString() }} people).
      </span>
      <span v-else>
        Between 2010 and 2020, {{ postgres.districtname }}'s population neither increased nor decreased.
      </span>
    </p>
    <p class="text-caption">
      <!-- Estimated population change is calculated by comparing the latest 2019
      American Community Survey district estimate with 2010 population counts
      (from <a href="https://www2.census.gov/census_2010/redistricting_file--pl_94-171/" target="_blank">the PL-94-171 file</a>). The 2010 data was allocated from census blocks to current district boundaries. -->
      Population change is calculated by comparing the district's <a href="https://www2.census.gov/programs-surveys/decennial/2020/data/01-Redistricting_File--PL_94-171/" target="_blank">2020 population</a> with <a href="https://www2.census.gov/census_2010/redistricting_file--pl_94-171/" target="_blank">2010 population
      counts</a> allocated from census blocks to current district
      boundaries.
    </p>
  </div>
</template>
<script>
import { Enums } from "../../../../data/Config/Config";
export default {
  name: "OnePOneVCongress",
  components: {},
  data: function () {
    return {};
  },
  mounted: function () {},
  methods: {},
  watch: {},
  computed: {
    postgres: function () {
      return (
        this.$store.getters.getSelectedData[Enums.DATA_SOURCE.POSTGRES] || {}
      );
    },
  },
};
</script>
<style></style>